import { z } from 'zod';
import { CONDICION_CLIENTE_EXTERIOR, MEDIOS_DE_LLEGADA, RFC_REGEX } from './constants';

// Looser than the PowerApps regex (which rejected .com.ar) to accept any
// well-formed email including Argentine TLDs.
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Phone formats Argentine users actually paste: bare digits ("11 1234 5678"),
// dashes, parens, leading "+54". Rejects letters so QA doesn't see "abc" land
// in Celular_C.
const phoneRegex = /^[\d\s\-+()]+$/;

// CUIT/CUIL is 11 digits, commonly written with dashes (XX-XXXXXXXX-X).
// We accept both forms; the stored value can keep the dashes for display.
const cuitRegex = /^\d{2}-?\d{8}-?\d{1}$/;

// Argentine "altura" is the street number — purely numeric.
const alturaRegex = /^\d+$/;

// MX SAT Código Postal — exactly 5 digits. Validated only when non-empty
// so AR contactos (which don't carry CP) stay valid.
const codigoPostalRegex = /^\d{5}$/;

export const contactSchema = z
  .object({
    // Datos del contacto
    nombre: z.string().min(1, 'Ingresá el nombre'),
    apellido: z.string().min(1, 'Ingresá el apellido'),
    mail: z
      .string()
      .trim()
      .optional()
      .refine((v) => !v || emailRegex.test(v), { message: 'Formato de email inválido' }),
    celular: z
      .string()
      .min(1, 'Ingresá un número de celular')
      .regex(phoneRegex, 'Solo se permiten números, espacios, guiones y paréntesis'),
    medioDeLlegada: z.enum(MEDIOS_DE_LLEGADA).optional(),
    referido: z.string().optional(),

    // Datos fiscales de facturación. `cuit` is the AR/MX tax-id slot —
    // accepts either CUIT (11 digits, AR) or RFC (12-13 alphanumeric, MX)
    // shape so the form works under either active country. The dialog
    // picks the right label and placeholder; we don't try to narrow by
    // country at the schema level (schemas are static, country is
    // runtime).
    cuit: z
      .string()
      .optional()
      .refine(
        (v) => {
          if (!v || !v.trim()) return true;
          const raw = v.trim();
          return cuitRegex.test(raw) || RFC_REGEX.test(raw);
        },
        { message: 'Formato inválido (CUIT en AR, RFC en MX)' },
      ),
    razonSocial: z.string().optional(),
    // Loosened from `z.enum(CONDICIONES_IVA)` / `z.enum(PROVINCIAS_ARGENTINA)`
    // to plain strings so MX values (Régimen Fiscal del SAT, Estados de
    // México) validate. The dialog UI restricts options per country —
    // schema just stores whatever the user picked.
    condicionIVA: z.string().optional(),
    provincia: z.string().optional(),
    ciudad: z.string().optional(),
    direccion: z.string().optional(),
    altura: z
      .string()
      .optional()
      .refine((v) => !v || !v.trim() || alturaRegex.test(v.trim()), {
        message: 'Solo números',
      }),
    piso: z.string().optional(),
    depto: z.string().optional(),
    // MX SAT fiscal data — used by Nova to stamp the CFDI 4.0 factura.
    // Stored on every contacto for forward-compat but only surfaced /
    // validated in the MX dialog. The schema accepts free text so AR
    // can write null without tripping; MX form-level validation is the
    // regex below.
    codigoPostal: z
      .string()
      .optional()
      .refine((v) => !v || !v.trim() || codigoPostalRegex.test(v.trim()), {
        message: 'Código Postal debe tener 5 dígitos',
      }),
    usoCfdi: z.string().optional(),

    // Datos de envío
    direccionCompleta: z.string().optional(),
    recibe: z.string().optional(),
    observaciones: z.string().optional(),

    // Cobranza (AR, sólo admin/administración). Vacío = parámetro general.
    pctMinimoEcheq: z
      .string()
      .optional()
      .refine(
        (v) => {
          if (!v || !v.trim()) return true;
          const n = Number(v.trim().replace(',', '.'));
          return Number.isFinite(n) && n >= 0 && n <= 100;
        },
        { message: 'Un porcentaje entre 0 y 100' },
      ),

    // Comisión de la vendedora (AR, sólo admin/administración). Apagado = regla general.
    comisionPropia: z.boolean(),
    comisionVendedor: z.string().optional(),

    // Cliente del Exterior (Factura E, ARCA WSFEX). Sólo se usan con Condición IVA
    // 'Cliente del Exterior'; el CUIT-país lo resuelve el server a partir de país + tipo de entidad.
    paisDestinoArca: z.string().optional(),
    tipoEntidadExterior: z.string().optional(),
    idImpositivoExterior: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.comisionPropia) {
      // numeric(5, 2) en la base: más decimales se redondearían sin avisar.
      const raw = val.comisionVendedor?.trim() ?? '';
      const n = Number(raw.replace(',', '.'));
      if (!/^\d+([.,]\d{1,2})?$/.test(raw) || n > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['comisionVendedor'],
          message: 'Un porcentaje entre 0 y 100, con hasta 2 decimales',
        });
      }
    }
    // Cliente del Exterior: sin estos tres datos no se puede emitir la Factura E (ni cobrar la venta).
    if (val.condicionIVA === CONDICION_CLIENTE_EXTERIOR) {
      const pedidos: Array<[keyof typeof val, string, string]> = [
        ['paisDestinoArca', 'País de destino', 'Elegí el país de destino'],
        ['tipoEntidadExterior', 'Tipo de entidad', 'Elegí el tipo de entidad'],
        ['idImpositivoExterior', 'ID impositivo', 'Ingresá el ID impositivo del cliente'],
      ];
      for (const [campo, , message] of pedidos) {
        const v = val[campo];
        if (typeof v !== 'string' || !v.trim()) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [campo], message });
        }
      }
    }
    if (val.medioDeLlegada === 'Referidos' && !(val.referido && val.referido.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['referido'],
        message: 'Ingresá quién refirió el contacto',
      });
    }
  });

export type ContactFormValues = z.infer<typeof contactSchema>;

export const emptyContact: ContactFormValues = {
  nombre: '',
  apellido: '',
  mail: '',
  celular: '',
  medioDeLlegada: undefined,
  referido: '',
  cuit: '',
  razonSocial: '',
  condicionIVA: undefined,
  provincia: undefined,
  ciudad: '',
  direccion: '',
  altura: '',
  piso: '',
  depto: '',
  codigoPostal: '',
  usoCfdi: undefined,
  direccionCompleta: '',
  recibe: '',
  observaciones: '',
  pctMinimoEcheq: '',
  comisionPropia: false,
  comisionVendedor: '',
  paisDestinoArca: undefined,
  tipoEntidadExterior: undefined,
  idImpositivoExterior: '',
};
